(() => {
  const DAY_MS = 24 * 60 * 60 * 1000;

  class VerriClubPopup {
    constructor(root) {
      this.root = root;
      this.backdrop = root.querySelector('[data-popup-backdrop]');
      this.dialog = root.querySelector('[data-popup-dialog]');
      this.form = root.querySelector('[data-popup-form]');
      this.emailInput = this.form?.querySelector('input[type="email"]');
      this.submitButton = root.querySelector('[data-popup-submit]');
      this.error = root.querySelector('[data-popup-error]');
      this.closeButtons = root.querySelectorAll('[data-popup-close]');
      this.laterButton = root.querySelector('[data-popup-later]');
      this.intro = root.querySelector('[data-popup-intro]');
      this.success = root.querySelector('[data-popup-success]');
      this.copyButton = root.querySelector('[data-popup-copy]');
      this.codeValue = root.querySelector('[data-popup-code]');
      // La linguetta sta fuori dal root (vedi snippet), quindi si cerca nel documento.
      this.tab = document.querySelector('[data-popup-tab]');

      this.customerConsent = root.dataset.customerConsent === 'true';
      this.isLoggedIn = root.dataset.customerLoggedIn === 'true';

      this.stateKey = 'vclubPopupState';
      this.sessionKey = 'vclubPopupSession';

      this.dismissCooldown = 7 * DAY_MS;
      this.laterCooldown = 2 * DAY_MS;
      this.subscribedCooldown = 365 * DAY_MS;
      this.triggerDelay = 20000;
      this.scrollThreshold = 0.5;

      this.isOpen = false;
      this.hasTriggered = false;
      this.isLoading = false;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleExitIntent = this.handleExitIntent.bind(this);
      this.handleEsc = this.handleEsc.bind(this);
      this.handleEmailInput = this.handleEmailInput.bind(this);
    }

    init() {
      // Explicit invites (e.g. Coffee Finder result CTA) bypass the scheduling
      // guards: a user click is intent, not an interruption.
      document.addEventListener('vclub:open', () => this.show(true));

      this.tab?.addEventListener('click', () => this.show(true));

      if (this.skipCompletely()) {
        this.updateTab();
        return;
      }

      this.bindEvents();
      this.updateSubmitState();
      this.scheduleTriggers();
      this.updateTab();
    }

    // La linguetta compare quando il popup non si mostrera' da solo ma
    // l'utente potrebbe ancora volerlo: dopo una chiusura (X, sfondo, Esc,
    // "Magari piu' tardi") o quando e' gia' uscito in questa sessione.
    // Mai per chi e' iscritto o ha gia' il consenso marketing.
    updateTab() {
      if (!this.tab) return;
      const state = this.getState();
      const eligible = !this.customerConsent && !state.subscribed;
      const dormant = this.hasCooldown(state.dismissedUntil) || this.hasSessionShown();
      this.tab.hidden = !(eligible && dormant && !this.isOpen);
    }

    skipCompletely() {
      const path = window.location.pathname;
      const isCartOrCheckout = path.startsWith('/cart') || path.startsWith('/checkout');
      const state = this.getState();

      return (
        isCartOrCheckout ||
        this.customerConsent ||
        state.subscribed ||
        this.hasCooldown(state.dismissedUntil)
      );
    }

    bindEvents() {
      if (this.eventsBound) return;
      this.eventsBound = true;
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.laterButton?.addEventListener('click', () => this.close(this.laterCooldown));
      this.backdrop?.addEventListener('click', () => this.close());

      if (this.form) {
        this.form.addEventListener('submit', (event) => this.handleSubmit(event));
      }

      if (this.emailInput) {
        this.emailInput.addEventListener('input', this.handleEmailInput);
      }

      this.copyButton?.addEventListener('click', () => this.copyCode());
    }

    // Dopo l'iscrizione il popup resta aperto sulla conferma con il codice:
    // chi arriva da un annuncio puo' usarlo subito, senza aspettare l'email.
    showSuccess() {
      if (!this.success) {
        this.close(this.subscribedCooldown);
        return;
      }
      if (this.intro) this.intro.hidden = true;
      this.success.hidden = false;
      this.dialog?.setAttribute('aria-labelledby', 'vclub-popup-success-title');
      this.success.querySelector('h2')?.setAttribute('tabindex', '-1');
      this.success.querySelector('h2')?.focus({ preventScroll: true });
      this.updateTab();
    }

    copyCode() {
      const code = this.codeValue?.textContent.trim();
      if (!code || !this.copyButton) return;
      const done = () => {
        const label = this.copyButton.textContent;
        this.copyButton.textContent = this.copyButton.dataset.copiedLabel || label;
        this.copyButton.disabled = true;
        window.setTimeout(() => {
          this.copyButton.textContent = label;
          this.copyButton.disabled = false;
        }, 2000);
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(done, done);
      } else {
        const range = document.createRange();
        range.selectNodeContents(this.codeValue);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        done();
      }
    }

    scheduleTriggers() {
      if (this.hasSessionShown()) return;

      window.setTimeout(() => this.show(), this.triggerDelay);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      document.addEventListener('mouseleave', this.handleExitIntent);
    }

    handleScroll() {
      if (this.hasTriggered) return;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = scrollable > 0 ? window.scrollY / scrollable : 0;
      if (ratio >= this.scrollThreshold) {
        this.show();
        window.removeEventListener('scroll', this.handleScroll);
      }
    }

    handleExitIntent(event) {
      if (this.hasTriggered || window.innerWidth < 768) return;
      if (event.clientY <= 0) {
        this.show();
        document.removeEventListener('mouseleave', this.handleExitIntent);
      }
    }

    handleEsc(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    }

    show(force = false) {
      if (force) {
        if (this.isOpen || this.getState().subscribed) return;
        this.bindEvents();
        this.updateSubmitState();
      } else {
        if (this.hasTriggered) return;
        if (!this.canOpen()) return;
      }

      this.hasTriggered = true;
      this.root.hidden = false;
      this.backdrop?.removeAttribute('hidden');
      this.dialog?.removeAttribute('hidden');
      this.root.classList.add('vclub-popup--open');
      document.body.classList.add('vclub-popup-locked');
      this.isOpen = true;
      this.markSessionShown();
      this.updateTab();
      this.clearError();
      this.emailInput?.focus({ preventScroll: true });
      this.updateSubmitState();

      document.addEventListener('keydown', this.handleEsc);
    }

    close(cooldown = this.dismissCooldown) {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.root.classList.remove('vclub-popup--open');
      document.body.classList.remove('vclub-popup-locked');
      document.removeEventListener('keydown', this.handleEsc);
      this.setCooldown(cooldown);

      window.setTimeout(() => {
        this.root.hidden = true;
        this.backdrop?.setAttribute('hidden', 'hidden');
        this.dialog?.setAttribute('hidden', 'hidden');
        this.updateTab();
      }, 220);
    }

    canOpen() {
      const state = this.getState();

      if (state.subscribed) return false;
      if (this.hasCooldown(state.dismissedUntil)) return false;
      if (this.hasSessionShown()) return false;

      return true;
    }

    hasCooldown(until) {
      const expires = Number(until);
      return Boolean(expires && expires > Date.now());
    }

    setCooldown(duration) {
      const until = Date.now() + Number(duration || 0);
      this.setState({ dismissedUntil: until });
    }

    handleSubmit(event) {
      event.preventDefault();

      if (!this.form || !this.emailInput) return;

      const email = this.emailInput.value.trim();
      if (!this.isValidEmail(email)) {
        this.showError(this.form.dataset.invalidMessage);
        return;
      }

      this.clearError();
      this.toggleLoading(true);

      const formData = new FormData(this.form);

      fetch(this.form.action, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'text/html,application/xhtml+xml',
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error('Request failed');
          this.setState({ subscribed: true, dismissedUntil: Date.now() + this.subscribedCooldown });
          this.showSuccess();
        })
        .catch(() => {
          // If the request errors in preview or due to blockers, still assume success to avoid blocking the user.
          this.setState({ subscribed: true, dismissedUntil: Date.now() + this.subscribedCooldown });
          this.showSuccess();
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    }

    handleEmailInput() {
      this.updateSubmitState();
    }

    toggleLoading(isLoading) {
      this.isLoading = isLoading;
      if (!this.submitButton) return;
      const valid = this.isValidEmail(this.emailInput?.value.trim() || '');
      this.submitButton.disabled = isLoading || !valid;
      this.submitButton.classList.toggle('button--loading', isLoading);
      this.submitButton.setAttribute('aria-busy', String(isLoading));
      this.submitButton.setAttribute('aria-disabled', String(this.submitButton.disabled));
    }

    showError(message) {
      if (!this.error) return;
      if (!message) {
        this.clearError();
        return;
      }
      this.error.textContent = message;
      this.error.removeAttribute('hidden');
    }

    clearError() {
      if (!this.error) return;
      this.error.setAttribute('hidden', 'hidden');
    }

    updateSubmitState() {
      if (!this.submitButton) return;
      const valid = this.isValidEmail(this.emailInput?.value.trim() || '');
      this.submitButton.disabled = this.isLoading || !valid;
      this.submitButton.setAttribute('aria-disabled', String(this.submitButton.disabled));
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    getState() {
      try {
        return JSON.parse(localStorage.getItem(this.stateKey)) || {};
      } catch (_error) {
        return {};
      }
    }

    setState(next) {
      const current = this.getState();
      const state = { ...current, ...next };
      try {
        localStorage.setItem(this.stateKey, JSON.stringify(state));
      } catch (_error) {
        /* ignore */
      }
      return state;
    }

    hasSessionShown() {
      try {
        return sessionStorage.getItem(this.sessionKey) === 'true';
      } catch (_error) {
        return false;
      }
    }

    markSessionShown() {
      try {
        sessionStorage.setItem(this.sessionKey, 'true');
      } catch (_error) {
        /* ignore */
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const popupRoot = document.getElementById('vclub-popup');
    if (!popupRoot) return;

    const popup = new VerriClubPopup(popupRoot);
    popup.init();
  });
})();
